/**
 * Listy do pól wyboru.
 *
 * Zwykłe `useQuery` przy błędzie zwraca `undefined`, a `data?.pozycje ?? []`
 * zamienia to w pustą rozwijkę. Użytkownik dostaje wtedy formularz, którego
 * nie da się wypełnić, i nie wie dlaczego — tak było, gdy interfejs prosił
 * o `naStronie=500`, a serwer przyjmował najwyżej 200.
 *
 * `useLista` podaje obok listy gotowy komunikat do `blad` w <Wybor>, więc
 * nieudane wczytanie widać przy polu zamiast pustego menu.
 */
import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import { BladApi } from './client';

export interface WynikListy<T> {
  /** Przy błędzie pusta — wtedy ustawiony jest `blad`. */
  pozycje: T[];
  /** Komunikat pod polem wyboru; `undefined`, gdy lista się wczytała. */
  blad?: string;
  wczytywanie: boolean;
}

/** Komunikat serwera jest konkretniejszy niż nasz zapasowy — bierzemy go, gdy jest. */
function komunikat(blad: unknown): string {
  return blad instanceof BladApi ? blad.message : 'Nie udało się wczytać listy.';
}

/** Jedna instancja pustej listy — żeby `useMemo` po `pozycje` nie liczył od nowa co render. */
const PUSTA: never[] = [];

export function useLista<O extends { pozycje: unknown[] }>(
  opcje: UseQueryOptions<O, Error, O, QueryKey>,
): WynikListy<O['pozycje'][number]> {
  const zapytanie = useQuery(opcje);
  return {
    pozycje: zapytanie.data?.pozycje ?? PUSTA,
    blad: zapytanie.isError ? komunikat(zapytanie.error) : undefined,
    wczytywanie: zapytanie.isLoading,
  };
}
